<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Driver Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.driver_name_search')"
                v-model="filterOptions.name"
              />
              <!-- End:: Driver Name Input -->

              <!-- Start:: Mobile Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.mobile')"
                v-model="filterOptions.mobile"
              />
              <!-- End:: Mobile Input -->

              <!-- Start:: Status Select -->
              <base-select-input
                col="3"
                :optionsList="statusOptions"
                :placeholder="$t('PLACEHOLDERS.driver_status')"
                v-model="filterOptions.is_active"
              />
              <!-- End:: Status Select -->

              <!-- Start:: Online Status Select -->
              <base-select-input
                col="3"
                :optionsList="onlineStatusOptions"
                :placeholder="$t('PLACEHOLDERS.connection_status')"
                v-model="filterOptions.is_online"
              />
              <!-- End:: Online Status Select -->

              <!-- Start:: Period Filter -->
              <base-select-input
                col="3"
                :optionsList="periodOptions"
                :placeholder="$t('PLACEHOLDERS.period_filter')"
                v-model="filterOptions.period"
              />
              <!-- End:: Period Filter -->

              <!-- Start:: Performance Filter -->
              <base-select-input
                col="3"
                :optionsList="performanceOptions"
                :placeholder="$t('PLACEHOLDERS.performance_filter')"
                v-model="filterOptions.performance"
              />
              <!-- End:: Performance Filter -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.drivers_reports") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
            :disabled="pdfDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serial`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Driver Name -->
        <template v-slot:[`item.name`]="{ item }">
          <div class="table_text_wrapper">
            <h6 class="text-danger" v-if="!item.name">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>{{ item.name }}</p>
          </div>
        </template>
        <!-- End:: Driver Name -->

        <!-- Start:: Mobile -->
        <template v-slot:[`item.mobile`]="{ item }">
          <div class="table_text_wrapper">
            <h6 class="text-danger" v-if="!item.mobile">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>{{ item.mobile }}</p>
          </div>
        </template>
        <!-- End:: Mobile -->

        <!-- Start:: Status -->
        <template v-slot:[`item.is_active`]="{ item }">
          <div class="table_text_wrapper">
            <span
              class="badge"
              :class="item.is_active ? 'badge-success' : 'badge-danger'"
            >
              {{
                item.is_active
                  ? $t("PLACEHOLDERS.active_status")
                  : $t("PLACEHOLDERS.inactive_status")
              }}
            </span>
          </div>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Online Status -->
        <template v-slot:[`item.is_online`]="{ item }">
          <div class="table_text_wrapper">
            <span
              class="badge"
              :class="item.is_online ? 'badge-success' : 'badge-secondary'"
            >
              {{
                item.is_online
                  ? $t("PLACEHOLDERS.online_status")
                  : $t("PLACEHOLDERS.offline_status")
              }}
            </span>
          </div>
        </template>
        <!-- End:: Online Status -->

        <!-- Start:: Total Delivered -->
        <template v-slot:[`item.total_delivered`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.total_delivered || 0 }}</p>
          </div>
        </template>
        <!-- End:: Total Delivered -->

        <!-- Start:: Total Rejected -->
        <template v-slot:[`item.total_rejected`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.total_rejected || 0 }}</p>
          </div>
        </template>
        <!-- End:: Total Rejected -->

        <!-- Start:: Rejection Rate -->
        <template v-slot:[`item.rejection_rate`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.rejection_rate || 0 }}%</p>
          </div>
        </template>
        <!-- End:: Rejection Rate -->

        <!-- Start:: Delay Rate -->
        <template v-slot:[`item.delay_rate`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.delay_rate || 0 }}%</p>
          </div>
        </template>
        <!-- End:: Delay Rate -->

        <!-- Start:: Speed Rate -->
        <template v-slot:[`item.speed_rate`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.speed_rate || 0 }}%</p>
          </div>
        </template>
        <!-- End:: Speed Rate -->

        <!-- Start:: Actions -->
        <template
          v-slot:[`item.actions`]="{ item }"
          v-if="$can('reports show', 'reports')"
        >
          <!-- v-if="$can('users show', 'users')" -->
          <a-tooltip placement="bottom">
            <template slot="title">
              <span>{{ $t("BUTTONS.show") }}</span>
            </template>
            <button class="btn_show" @click="showItem(item)">
              <i class="fal fa-eye"></i>
            </button>
          </a-tooltip>

          <!-- <template v-else>
            <i class="fal fa-lock-alt fs-5 blue-grey--text text--darken-1"></i>
          </template> -->
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('PLACEHOLDERS.drivers_reports')"
      :pdf-quality="2"
      pdf-format="a3"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @progress="pdfDownloadBtnIsLoading = true"
      @hasGenerated="handlePdfGenerated"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">
            {{ $t("PLACEHOLDERS.drivers_reports") }}
          </h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <template v-for="(header, index) in tableHeaders">
                    <th
                      class="text-center"
                      :key="index"
                      v-if="header.value !== 'actions'"
                    >
                      {{ header.text }}
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in tableRows" :key="index">
                  <td class="text-center">
                    {{ index + 1 }}
                  </td>
                  <td class="text-center">
                    {{ item.name }}
                  </td>
                  <td class="text-center">
                    {{ item.mobile }}
                  </td>
                  <td class="text-center">
                    {{
                      item.is_active
                        ? $t("PLACEHOLDERS.active_status")
                        : $t("PLACEHOLDERS.inactive_status")
                    }}
                  </td>
                  <td class="text-center">
                    {{
                      item.is_online
                        ? $t("PLACEHOLDERS.online_status")
                        : $t("PLACEHOLDERS.offline_status")
                    }}
                  </td>
                  <td class="text-center">
                    {{ item.total_delivered || 0 }}
                  </td>
                  <td class="text-center">
                    {{ item.total_rejected || 0 }}
                  </td>
                  <td class="text-center">{{ item.rejection_rate || 0 }}%</td>
                  <td class="text-center">{{ item.delay_rate || 0 }}%</td>
                  <td class="text-center">{{ item.speed_rate || 0 }}%</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </section>
    </vue-html2pdf>
    <!-- End:: Generate PDF Template Content -->
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";

export default {
  name: "AllDriversReports",

  components: {
    VueHtml2pdf,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    statusOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.active_status"),
          value: 1,
        },
        {
          id: 0,
          name: this.$t("PLACEHOLDERS.inactive_status"),
          value: 0,
        },
      ];
    },

    onlineStatusOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.online_status"),
          value: 1,
        },
        {
          id: 0,
          name: this.$t("PLACEHOLDERS.offline_status"),
          value: 0,
        },
      ];
    },

    periodOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.daily_period"),
          value: "daily",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.weekly_period"),
          value: "weekly",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.monthly_period"),
          value: "monthly",
        },
      ];
    },

    performanceOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.yes"),
          value: 1,
        },
        {
          id: 0,
          name: this.$t("PLACEHOLDERS.no"),
          value: 0,
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      pdfDownloadBtnIsLoading: false,
      excelDownloadBtnIsLoading: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        mobile: null,
        is_active: null,
        is_online: null,
        period: null,
        performance: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "serial",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.driver_name"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.mobile"),
          value: "mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.driver_status"),
          value: "is_active",
          sortable: false,
          align: "center",
        },
        // {
        //   text: this.$t("PLACEHOLDERS.connection_status"),
        //   value: "is_online",
        //   align: "center",
        //   sortable: false,
        // },
        {
          text: this.$t("PLACEHOLDERS.total_delivered"),
          value: "total_delivered",
          sortable: false,
          align: "center",
        },
        // {
        //   text: this.$t("PLACEHOLDERS.total_rejected"),
        //   value: "total_rejected",
        //   sortable: false,
        //   align: "center",
        // },
        // {
        //   text: this.$t("PLACEHOLDERS.rejection_rate"),
        //   value: "rejection_rate",
        //   sortable: false,
        //   align: "center",
        // },
        {
          text: this.$t("PLACEHOLDERS.delay_rate"),
          value: "delay_rate",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.speed_rate"),
          value: "speed_rate",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/drivers-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.mobile = null;
      this.filterOptions.is_active = null;
      this.filterOptions.is_online = null;
      this.filterOptions.period = null;
      this.filterOptions.performance = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/drivers-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: "reports/drivers",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            mobile: this.filterOptions.mobile,
            is_active: this.filterOptions.is_active,
            connection_status: this.filterOptions.is_online?.value,
            period: this.filterOptions.period?.value,
            best_performance: this.filterOptions?.performance?.value,
          },
        });

        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },

    async downloadExcel() {
      this.excelDownloadBtnIsLoading = true;

      try {
        const res = await this.$axios({
          method: "GET",
          url: "reports/drivers",
          params: {
            format: "excel",
            name: this.filterOptions.name,
            mobile: this.filterOptions.mobile,
            is_active: this.filterOptions.is_active?.value,
            connection_status: this.filterOptions.is_online?.value,
            period: this.filterOptions.period?.value,
            best_performance: this.filterOptions?.performance?.value,
            page: this.paginations.current_page,
          },
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${this.$t(
            "PLACEHOLDERS.drivers_reports"
          )}_${new Date().getTime()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.excelDownloadBtnIsLoading = false;
        this.$message.success(this.$t("MESSAGES.downloadedSuccessfully"));
      } catch (error) {
        this.excelDownloadBtnIsLoading = false;
        console.log(error.response?.data?.message);
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
      }
    },

    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },

    handlePdfGenerated() {
      this.pdfDownloadBtnIsLoading = false;
      this.$message.success(this.$t("MESSAGES.generatedSuccessfully"));
    },

    showItem(item) {
      // Implement show driver details functionality
      this.$router.push({ path: `/DeliveryMen/show/${item.id}` });
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
  },
};
</script>

<style lang="scss" scoped>
.badge {
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;

  &.badge-success {
    background-color: #4caf50;
    color: white;
  }

  &.badge-danger {
    background-color: #f44336;
    color: white;
  }

  &.badge-secondary {
    background-color: #9e9e9e;
    color: white;
  }
}

.pdf_file_content {
  padding: 20px;
  background-color: white;

  .file_title {
    text-align: center;
    margin-bottom: 20px;
    color: #333;
  }
}

// Dark Theme Support
:root .dark_theme {
  .pdf_file_content {
    background-color: var(--theme_second_bg_clr);

    .file_title {
      color: var(--theme_text_clr);
    }
  }
}
</style>
